import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  FileCheck,
  Inbox,
  Calendar,
  Receipt,
} from "lucide-react";
import Pagination from "../../../components/common/Pagination";
import {
  getAllPaymentRequests,
  deletePaymentRequest,
  approvePayment,
  rejectPayment,
  submitPaymentRequest,
} from "../../../api/finance/payment/paymentRequests";
import {
  showErrorToast,
  showSuccessToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import PaymentFilter from "./PaymentFilter";
import PaymentCreateModal from "./PaymentCreateModal";
import PaymentDetailModal from "./PaymentDetailModal";
import PaymentSummaryCards from "./PaymentSummaryCards";
import PendingPaymentList from "./PendingPaymentList";
import { sortPendingFinanceCards } from "../shared/sortPendingFinanceCards";
import PaymentRowActionMenu from "./PaymentRowActionMenu";
import PaymentPrintModal from "./PaymentPrintModal";
import { formatDateVN } from "../../../utils/formatUtils";
import { hasPermission } from "../../../utils/permissionUtils";
import { currentPeriodDefaults } from "../../reports/finance/financePeriodUtils";

const PaymentListPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const period = currentPeriodDefaults();
    return {
      keyword: "",
      branch_id: "",
      department_id: "",
      status: "",
      period_type: period.period_type,
      year: period.year,
      quarter: period.quarter,
      month: period.month,
      from_date: period.from_date,
      to_date: period.to_date,
    };
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [copyFromId, setCopyFromId] = useState(null);
  const [printId, setPrintId] = useState(null);
  const [openActionId, setOpenActionId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllPaymentRequests();
      setData(res || []);
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải danh sách đề nghị thanh toán.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const scopedData = useMemo(() => {
    return data.filter((item) => {
      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase();
        const code = (item.request_code || item.payment_code || "").toLowerCase();
        const ok =
          code.includes(keyword) ||
          item.employee_name?.toLowerCase().includes(keyword) ||
          item.payment_reason?.toLowerCase().includes(keyword);
        if (!ok) return false;
      }

      if (filters.branch_id && item.branch_id && item.branch_id !== filters.branch_id)
        return false;

      if (
        filters.department_id &&
        item.department_id &&
        item.department_id !== filters.department_id
      )
        return false;

      const rd = item.payment_date
        ? String(item.payment_date).slice(0, 10)
        : item.created_at
          ? String(item.created_at).slice(0, 10)
          : "";
      if (filters.from_date && rd && rd < filters.from_date) return false;
      if (filters.to_date && rd && rd > filters.to_date) return false;

      return true;
    });
  }, [
    data,
    filters.keyword,
    filters.branch_id,
    filters.department_id,
    filters.from_date,
    filters.to_date,
  ]);

  const filteredData = useMemo(() => {
    if (!filters.status) return scopedData;
    return scopedData.filter((item) => item.status === filters.status);
  }, [scopedData, filters.status]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const pendingForCards = useMemo(
    () =>
      sortPendingFinanceCards(
        scopedData.filter((x) => x.status === "waiting")
      ),
    [scopedData]
  );

  const summaryData = useMemo(() => {
    const summary = {
      draft: { count: 0, amount: 0 },
      waiting: { count: 0, amount: 0 },
      approved: { count: 0, amount: 0 },
      rejected: { count: 0, amount: 0 },
      completed: { count: 0, amount: 0 },
    };

    scopedData.forEach((item) => {
      if (!summary[item.status]) return;
      summary[item.status].count += 1;
      summary[item.status].amount += Number(item.payment_amount || 0);
    });

    return summary;
  }, [scopedData]);

  const mobileStatusTone = (status) => {
    switch (status) {
      case "waiting":
        return {
          card: "bg-amber-50 border-amber-300",
          divider: "border-amber-200/80",
          amount: "text-amber-700",
        };
      case "approved":
        return {
          card: "bg-emerald-50 border-emerald-300",
          divider: "border-emerald-200/80",
          amount: "text-emerald-700",
        };
      case "rejected":
        return {
          card: "bg-red-50 border-red-300",
          divider: "border-red-200/80",
          amount: "text-red-700",
        };
      case "completed":
        return {
          card: "bg-blue-50 border-blue-300",
          divider: "border-blue-200/80",
          amount: "text-blue-700",
        };
      case "draft":
      default:
        return {
          card: "bg-slate-50 border-slate-300",
          divider: "border-slate-200/80",
          amount: "text-slate-700",
        };
    }
  };

  const getStatusBadge = (status) => {
    let label = "Không xác định";
    let colorClass = "bg-gray-50 text-gray-500 border-gray-100";

    switch (status) {
      case "draft":
        label = "Nháp";
        colorClass = "bg-slate-100 text-slate-600 border-slate-200";
        break;
      case "waiting":
        label = "Chờ duyệt";
        colorClass = "bg-amber-50 text-amber-700 border-amber-100";
        break;
      case "approved":
        label = "Đã duyệt";
        colorClass = "bg-emerald-50 text-emerald-700 border-emerald-100";
        break;
      case "rejected":
        label = "Từ chối";
        colorClass = "bg-red-50 text-red-700 border-red-100";
        break;
      case "completed":
        label = "Hoàn thành";
        colorClass = "bg-blue-50 text-blue-700 border-blue-100";
        break;
      default:
        break;
    }

    return (
      <span
        className={`inline-flex shrink-0 max-w-[7.5rem] truncate px-2 py-0.5 md:px-3 md:py-1 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-wider border whitespace-nowrap ${colorClass}`}
        title={label}
      >
        {label}
      </span>
    );
  };

  const docDate = (item) =>
    item.payment_date
      ? formatDateVN(item.payment_date)
      : item.created_at
        ? formatDateVN(item.created_at)
        : "—";

  const requestCode = (item) => item.request_code || item.payment_code || "—";

  const openCreateDrawer = ({ copyFrom = null } = {}) => {
    setCopyFromId(copyFrom);
    setShowDrawer(true);
  };

  const handleViewItem = (item) => setSelectedItem(item);

  const handleDownloadItem = (item) => setPrintId(item?.id || null);

  const handleCopyItem = (item) => {
    if (!hasPermission("finance.payment_requests", "C")) {
      showErrorToast("Bạn không có quyền tạo phiếu thanh toán.");
      return;
    }
    openCreateDrawer({ copyFrom: item.id });
  };

  const handleEditItem = () => {
    showErrorToast(
      "Phiếu thanh toán chưa hỗ trợ sửa. Vui lòng dùng Sao chép để tạo phiếu mới."
    );
  };

  const handleDelete = async (id) => {
    const confirm = await showConfirmDialog({
      title: "Hủy đề nghị thanh toán",
      text: "Bạn có chắc muốn hủy đề nghị này? Thao tác không hoàn tác.",
      confirmText: "Hủy đề nghị",
      cancelText: "Giữ lại",
    });
    if (!confirm?.isConfirmed) return;

    try {
      await deletePaymentRequest(id);
      showSuccessToast("Đã xóa đề nghị.");
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Không thể xóa đề nghị."
      );
    }
  };

  const handleUpdateStatus = async (id, status, rejectReason) => {
    try {
      if (status === "submitted") {
        await submitPaymentRequest(id);
      }
      if (status === "approved") await approvePayment(id);
      if (status === "rejected") await rejectPayment(id, rejectReason);
      showSuccessToast(
        status === "submitted"
          ? "Đã gửi đề nghị đi duyệt"
          : "Cập nhật trạng thái thành công"
      );
      fetchData();
      setSelectedItem(null);
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Không thể cập nhật trạng thái"
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans">
      <div className="p-4 md:p-6">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-red-700 p-2.5 rounded-xl text-white shadow-lg shadow-red-200">
              <FileCheck size={24} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-black text-gray-800 tracking-tight uppercase">
                <span className="md:hidden">
                  Đề nghị <span className="text-red-700">thanh toán</span>
                </span>
                <span className="hidden md:inline">
                  Quản lý <span className="text-red-700">Đề nghị thanh toán</span>
                </span>
              </h1>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">
                Phê duyệt nhiều cấp
              </p>
            </div>
          </div>
        </header>

        <main>
          <PaymentSummaryCards
            summaryData={summaryData}
            selectedStatus={filters.status}
            onSelectStatus={(status) =>
              setFilters((prev) => ({ ...prev, status }))
            }
          />

          <PaymentFilter
            onFilterChange={(f) => setFilters(f)}
            onNewClick={() => openCreateDrawer()}
            onRefresh={fetchData}
            status={filters.status}
          />

          <PendingPaymentList
            data={pendingForCards}
            onSelect={(item) => setSelectedItem(item)}
          />

          <h2 className="font-semibold text-sm md:text-base text-gray-700 mb-3 ml-1 md:ml-2">
            Bảng danh sách phiếu thanh toán ({filteredData.length} phiếu)
          </h2>

          {/* Mobile: card list — chỉ xem */}
          <div className="md:hidden space-y-3 relative min-h-[120px]">
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
                <div className="animate-spin w-9 h-9 border-2 border-red-600 border-t-transparent rounded-full" />
              </div>
            )}
            {!loading && paginatedData.length === 0 && (
              <div className="bg-white rounded-2xl border-2 border-gray-300 px-4 py-12 text-center">
                <Inbox className="w-10 h-10 mx-auto text-gray-200 mb-2" strokeWidth={1} />
                <p className="text-gray-400 text-sm italic">Không có dữ liệu phù hợp.</p>
              </div>
            )}
            {paginatedData.map((item) => {
              const tone = mobileStatusTone(item.status);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedItem(item)}
                  className={`w-full text-left rounded-2xl border-2 shadow-sm p-4 active:scale-[0.99] transition-transform ${tone.card}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="text-sm font-black text-gray-800 uppercase tracking-tight truncate">
                        {requestCode(item)}
                      </p>
                      <p className="text-xs text-gray-700 mt-0.5 truncate font-medium">
                        {item.employee_name || "—"}
                      </p>
                      <p className="text-[10px] text-gray-500 font-bold mt-1 flex items-center gap-1">
                        <Calendar size={10} className="shrink-0" />
                        {docDate(item)}
                        {item.branch_name ? (
                          <span className="truncate"> · {item.branch_name}</span>
                        ) : null}
                      </p>
                    </div>
                    {getStatusBadge(item.status)}
                  </div>
                  <div className={`flex items-end justify-between gap-3 pt-3 border-t ${tone.divider}`}>
                    <p className="text-xs text-gray-600 min-w-0 line-clamp-2 whitespace-pre-wrap break-words">
                      {item.payment_reason || "—"}
                    </p>
                    <p className={`text-base font-black tabular-nums shrink-0 ${tone.amount}`}>
                      {Number(item.payment_amount || 0).toLocaleString("vi-VN")} ₫
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto relative min-h-[200px]">
              {loading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-[2rem]">
                  <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
                </div>
              )}
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-14">
                      STT
                    </th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400">
                      Mã phiếu
                    </th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400">
                      Nhân viên / Đơn vị
                    </th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400 whitespace-nowrap">
                      Số tiền
                    </th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400">
                      Lý do
                    </th>
                    <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400 text-left min-w-[8.5rem] whitespace-nowrap">
                      Trạng thái
                    </th>
                    <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-16">
                      File
                    </th>
                    <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-[72px]">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {!loading && paginatedData.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-16 text-center">
                        <Inbox
                          className="w-12 h-12 mx-auto text-gray-200 mb-3"
                          strokeWidth={1}
                        />
                        <p className="text-gray-400 text-sm font-medium italic">
                          Không có dữ liệu phù hợp bộ lọc.
                        </p>
                      </td>
                    </tr>
                  )}
                  {paginatedData.map((item, index) => (
                    <tr
                      key={item.id}
                      onClick={() => setSelectedItem(item)}
                      className="group hover:bg-red-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-4 text-center text-xs font-bold text-gray-400 align-top">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-start gap-2">
                          <Receipt
                            size={16}
                            className="text-gray-300 group-hover:text-red-400 mt-0.5 shrink-0"
                          />
                          <div className="min-w-0">
                            <span className="text-sm font-bold text-gray-800 group-hover:text-red-700 uppercase tracking-tight">
                              {requestCode(item)}
                            </span>
                            <div className="flex items-center gap-1 text-[11px] text-gray-400 font-semibold mt-1">
                              <Calendar size={11} className="shrink-0" />
                              {docDate(item)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-xl bg-red-50 text-red-700 flex items-center justify-center text-xs font-black group-hover:bg-red-100 shrink-0">
                            {item.employee_name?.split(" ").pop()?.charAt(0) ||
                              "?"}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-800">
                              {item.employee_name || "—"}
                            </div>
                            <div className="text-[10px] text-gray-500 uppercase font-medium">
                              {item.branch_name || "—"} •{" "}
                              {item.department_name || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top whitespace-nowrap">
                        <div className="text-sm font-black text-red-700 tabular-nums">
                          {Number(item.payment_amount || 0).toLocaleString("vi-VN")} ₫
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="max-w-[280px] text-sm text-gray-600 whitespace-pre-wrap break-words leading-relaxed">
                          {item.payment_reason || "—"}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top text-left min-w-[8.5rem]">
                        {getStatusBadge(item.status)}
                      </td>
                      <td
                        className="px-4 py-4 align-top text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="text-xs text-gray-300">—</span>
                      </td>
                      <td
                        className="px-4 py-4 align-top text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PaymentRowActionMenu
                          item={item}
                          isOpen={openActionId === `desktop-${item.id}`}
                          onOpenChange={(open) =>
                            setOpenActionId(open ? `desktop-${item.id}` : null)
                          }
                          onView={handleViewItem}
                          onDownload={handleDownloadItem}
                          onCopy={handleCopyItem}
                          onEdit={handleEditItem}
                          onDelete={(row) => handleDelete(row.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredData.length > 0 && (
            <Pagination
              totalRecords={filteredData.length}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </main>
      </div>

      <PaymentDetailModal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        paymentId={selectedItem?.id}
        onUpdateStatus={handleUpdateStatus}
        onReminded={fetchData}
      />

      <PaymentCreateModal
        key={copyFromId ? `copy-${copyFromId}` : "create"}
        isOpen={showDrawer}
        copyFromId={copyFromId}
        onClose={() => {
          setShowDrawer(false);
          setCopyFromId(null);
        }}
        onSaved={() => {
          fetchData();
          setShowDrawer(false);
          setCopyFromId(null);
        }}
      />

      <PaymentPrintModal
        isOpen={!!printId}
        paymentId={printId}
        onClose={() => setPrintId(null)}
      />
    </div>
  );
};

export default PaymentListPage;
